import type { PaigeClient } from '@paige/api-client';
import type { FleetApi } from './data-source';
import type { Fleet, InboundMessage, JobDetail } from './types';
import { tenantHeader, unwrap } from './http';

/** Sources: list (the email source is always among them), add, edit, release a hold. */
function sourceApi(
  client: PaigeClient,
): Pick<FleetApi, 'listSources' | 'addSource' | 'updateSource' | 'releaseSource'> {
  return {
    async listSources(tenant, jobId) {
      const r = await client.GET('/jobs/{jobId}/sources', {
        params: { header: tenantHeader(tenant), path: { jobId } },
      });
      return unwrap(r);
    },
    async addSource(tenant, jobId, input) {
      const r = await client.POST('/jobs/{jobId}/sources', {
        params: { header: { ...tenantHeader(tenant), 'Idempotency-Key': '' }, path: { jobId } },
        body: input,
      });
      return unwrap(r);
    },
    async updateSource(tenant, jobId, sourceId, input) {
      const r = await client.PUT('/jobs/{jobId}/sources/{sourceId}', {
        params: {
          header: { ...tenantHeader(tenant), 'Idempotency-Key': '' },
          path: { jobId, sourceId },
        },
        body: input,
      });
      return unwrap(r);
    },
    async releaseSource(tenant, jobId, sourceId) {
      const r = await client.POST('/jobs/{jobId}/sources/{sourceId}/release', {
        params: {
          header: { ...tenantHeader(tenant), 'Idempotency-Key': '' },
          path: { jobId, sourceId },
        },
      });
      return unwrap(r);
    },
  };
}

/** An email source's recent messages. */
function messageApi(client: PaigeClient): Pick<FleetApi, 'listSourceMessages'> {
  return {
    async listSourceMessages(tenant, jobId, sourceId) {
      const r = await client.GET('/jobs/{jobId}/sources/{sourceId}/messages', {
        params: { header: tenantHeader(tenant), path: { jobId, sourceId } },
      });
      return unwrap(r) as InboundMessage[];
    },
  };
}

/** Creating, renaming and deleting a job, and retrying a destination. */
function jobWrites(
  client: PaigeClient,
): Pick<FleetApi, 'createJob' | 'renameJob' | 'deleteJob' | 'retryDestination'> {
  return {
    async renameJob(tenant, jobId, name) {
      const r = await client.PATCH('/jobs/{jobId}', {
        params: { header: tenantHeader(tenant), path: { jobId } },
        body: { name },
      });
      return unwrap(r);
    },
    async deleteJob(tenant, jobId) {
      const r = await client.DELETE('/jobs/{jobId}', {
        params: { header: tenantHeader(tenant), path: { jobId } },
      });
      unwrap(r);
    },
    async createJob(_tenant, name) {
      const r = await client.POST('/jobs', {
        params: { header: { 'Idempotency-Key': '' } },
        body: { name },
      });
      return unwrap(r);
    },
    async retryDestination(tenant, jobId, destinationId) {
      const r = await client.POST('/jobs/{jobId}/destinations/{destinationId}/retry', {
        params: {
          header: { ...tenantHeader(tenant), 'Idempotency-Key': '' },
          path: { jobId, destinationId },
        },
      });
      unwrap(r);
    },
  };
}

/** Level 1 and Level 2 reads and the explicit transitions, over the generated client. */
export function fleetApi(client: PaigeClient): FleetApi {
  return {
    ...sourceApi(client),
    ...messageApi(client),
    ...jobWrites(client),
    async getFleet(tenant) {
      const r = await client.GET('/overview', { params: { header: tenantHeader(tenant) } });
      return unwrap(r) as Fleet;
    },
    async getJob(tenant, jobId) {
      const r = await client.GET('/jobs/{jobId}/overview', {
        params: { header: tenantHeader(tenant), path: { jobId } },
      });
      return unwrap(r) as JobDetail;
    },
    async setJobState(tenant, jobId, state) {
      const r = await client.POST('/jobs/{jobId}/state', {
        params: { header: { ...tenantHeader(tenant), 'Idempotency-Key': '' }, path: { jobId } },
        body: { state },
      });
      return unwrap(r) as JobDetail;
    },
    async listExceptions(tenant, jobId) {
      const r = await client.GET('/jobs/{jobId}/exceptions', {
        params: { header: tenantHeader(tenant), path: { jobId } },
      });
      return unwrap(r);
    },
  };
}
